import RangeField from '@/components/fields/RangeField';
import SelectField, {
	ISelectFieldOption,
} from '@/components/fields/SelectField';
import { confirmModal } from '@/components/modal/Modal';
import { useKeycloak } from '@/contexts/KeycloakContext';
import { Button } from '@/styles/buttons';
import { ScenarioConfig, ScenarioConfigItem } from '@/styles/scenario';
import axios from '@/utils/axios';
import { buildParams, currentYear } from '@/utils/utils';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

interface CapacityConfigProps {
	setScenarioData: React.Dispatch<
		React.SetStateAction<TCapacityScenarioData[]>
	>;
	selectedItem: TCapacityTreeSelectedItem | undefined;
	scenarioId: TValue | undefined;
	scenarios: ISelectFieldOption[] | undefined;
	isProductionRegionSelected: boolean;
	min: number;
	max: number;
	yearRange: [number, number];
	setYearRange: React.Dispatch<React.SetStateAction<[number, number]>>;
	currentScenarioRange: [number, number] | null;
}

const CapacityConfig: React.FC<CapacityConfigProps> = ({
	setScenarioData,
	scenarioId,
	scenarios,
	selectedItem,
	isProductionRegionSelected,
	min,
	max,
	yearRange,
	setYearRange,
	currentScenarioRange,
}) => {
	const queryClient = useQueryClient();
	const { keycloak } = useKeycloak();
	const [scenarioIdFrom, setScenarioIdFrom] = useState<TValue>();

	const { mutate: reset } = useMutation(
		['reset_capacity'],
		() =>
			axios.post('/capacity_updates', {
				capacity_scenario_id: scenarioId,
				last_modified_by: keycloak?.tokenParsed?.preferred_username,
				last_modified_datetime: new Date(),
				production_region: selectedItem!.item.production_region,
				...(!isProductionRegionSelected && {
					plant_code: selectedItem!.item.plant_code,
				}),
			}),
		{
			onSuccess: ({ data }) => {
				setScenarioData(data);
			},
		}
	);

	const { mutate: copyUpdates } = useMutation(
		['capacity_copy_updates'],
		() =>
			axios.post(
				'/capacity_scenario/copyupdates' +
					buildParams({
						scenario_id_from: scenarioIdFrom,
						scenario_id_to: scenarioId,
						user: keycloak?.tokenParsed?.preferred_username,
						production_region: selectedItem!.item.production_region,
						...(!isProductionRegionSelected
							? {
									plant_code: selectedItem!.item.plant_code,
							  }
							: undefined),
					})
			),
		{
			onSuccess: () => {
				queryClient.invalidateQueries([
					'capacity_scenario',
					scenarioId,
					selectedItem?.value,
				]);
				setScenarioIdFrom(undefined);
			},
		}
	);

	return selectedItem ? (
		<ScenarioConfig>
			<ScenarioConfigItem
				style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
				<Button
					onClick={async () => {
						if (
							await confirmModal({
								content: `Are you sure you want to reset this region : "${selectedItem.value}" ?`,
							})
						) {
							reset();
						}
					}}>
					Reset to initial values
				</Button>
			</ScenarioConfigItem>
			<ScenarioConfigItem
				style={{
					flex: 1,
					display: 'flex',
					justifyContent: 'center',
					gap: '1rem',
				}}>
				<Button
					disabled={!scenarioIdFrom}
					onClick={async () => {
						if (
							await confirmModal({
								content: `Are you sure you wanto apply the update to the scenario : "${
									scenarios?.find(
										(scenario) => scenario.value === scenarioIdFrom
									)?.label
								}"?`,
							})
						) {
							copyUpdates();
						}
					}}>
					Apply to the hierarchy
				</Button>
				<SelectField
					options={
						scenarios?.filter((scenario) => scenario.value !== scenarioId) ?? []
					}
					title='Load a scenario'
					value={scenarioIdFrom}
					setValue={setScenarioIdFrom}
				/>
			</ScenarioConfigItem>
			<ScenarioConfigItem
				style={{
					flex: 1,
					display: 'flex',
					justifyContent: 'center',
				}}>
				<RangeField
					min={min}
					max={max}
					value={yearRange}
					blockMinAt={currentYear()}
					onRelease={(v) => {
						setYearRange(v);
						queryClient.invalidateQueries([
							'capacity_scenario',
							scenarioId,
							selectedItem?.value,
							yearRange,
						]);
					}}
					setValueOnRelease
				/>
			</ScenarioConfigItem>
		</ScenarioConfig>
	) : null;
};

export default CapacityConfig;
